/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ITextField, ITextFieldProps, TextField } from '@fluentui/react/lib/TextField';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { LuisKeyCodes } from 'src/core/utils/keyCodes';

export type LuisTextFieldProps = ITextFieldProps & {
    /**
     * A handy callback for when the enter key
     * is pressed.
     */
    onEnterKeyUp?: (event?: React.KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => void;

    /**
     * ID for field's selection in E2E Tests.
     * HTML attribute: data-automation-id
     */
    'data-automation-id'?: string;

    /**
     *  Handle the time taken to change the field.
     *  This time is updated each time the field is changed.
     */
    onTimeTakenChange?: (time: number) => void;
};

/**
 * The localization messages to view in the luis text field.
 */
const messages = defineMessages({
    defaultPlaceholder: {
        id: 'core.luisTextField.defaultPlaceholder',
        defaultMessage: 'Type text here ...'
    },
    defaultAriaLabel: {
        id: 'core.luisTextField.defaultAriaLabel',
        defaultMessage: 'Textfield'
    }
});

/**
 * Creates a text field with common needed properties across
 * the portal.
 *
 * @param props The text field props passed.
 */
export const LuisTextField = React.forwardRef((props: LuisTextFieldProps, tagInputFieldRef: React.MutableRefObject<ITextField>) => {
    const { onEnterKeyUp, onKeyDown, onChange, onFocus, onBlur, onTimeTakenChange, componentRef, errorMessage, ...rest } = props;
    const { localize } = useLocalization();
    const [isFieldChanged, setIsFieldChanged] = React.useState<boolean>(false);
    const [lastTypingTimestamp, setLastTypingTimestamp] = React.useState<number>(0);
    const { placeholder = localize(messages.defaultPlaceholder) } = props;

    /**
     * Intercepts the key up event to handle the Enter key press.
     *
     * @param event The event object that was passed with the event.
     */
    const interceptKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (onEnterKeyUp && event.key === LuisKeyCodes.Enter) {
            onEnterKeyUp(event);
        }

        // Pass through the event if it was defined.
        if (onKeyDown) {
            onKeyDown(event);
        }
    };

    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
        if (onTimeTakenChange) {
            setLastTypingTimestamp(Date.now());
        }

        onFocus?.(event);
    };

    const handleOnChange = (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string) => {
        if (onTimeTakenChange) {
            setIsFieldChanged(true);
        }
        onChange?.(event, newValue);
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement, Element>) => {
        if (onTimeTakenChange) {
            const currentTime = Date.now();
            if (isFieldChanged) {
                onTimeTakenChange(currentTime - lastTypingTimestamp);
            } else {
                onTimeTakenChange(0);
            }

            setIsFieldChanged(false);
        }
        onBlur?.(event);
    };
    /**
     * The reason we wrap the errorMessage into the onGetErrorMessage function
     * is that ensures that the validation properties that the fabric library
     * support are applied. Also, error messages by our validators return undefined
     * if they succeed, while fabric's libraries validation success expects an empty string.
     */
    return (
        <TextField
            {...rest}
            componentRef={tagInputFieldRef || componentRef}
            placeholder={placeholder}
            errorMessage={errorMessage || ''}
            deferredValidationTime={300}
            autoComplete="off"
            onBlur={handleOnBlur}
            onFocus={handleOnFocus}
            onChange={handleOnChange}
            onKeyDown={interceptKeyDown}
            ariaLabel={props.ariaLabel || props.label || localize(messages.defaultAriaLabel)}
        />
    );
});
